import Client from 'clients/base/Client'
import { useCore } from 'contexts/core-context'
import { useGlobal } from 'contexts/global-context'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button, Table } from 'ui-toolkit-tailwind/src/components'
import { showToast } from 'utilities/showToast'

import {
	areAuditQueryStatesEqual,
	buildAuditPagination,
	createAuditSearchParams,
	DEFAULT_FILTERS,
	DEFAULT_PAGE,
	DEFAULT_PAGE_SIZE,
	DEFAULT_SORT_BY,
	DEFAULT_SORT_DIR,
	getAuditRowRange,
	getNextSortState,
	normalizeAuditFilters,
	PAGE_SIZE_OPTIONS,
	parseAuditSearchParams,
	SORTABLE_COLUMNS} from './queryState'

const inputClassName =
	'block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm outline-none transition focus:border-[#3E63F4] focus:ring-2 focus:ring-[#3E63F4]/20'

const defaultAuditState = {
	items: [],
	page: DEFAULT_PAGE,
	pageSize: DEFAULT_PAGE_SIZE,
	totalItems: 0,
	totalPages: 1,
	sortBy: DEFAULT_SORT_BY,
	sortDir: DEFAULT_SORT_DIR,
	statusOptions: [],
	emailTypeOptions: []
}

const formatTimestamp = value => {
	if (!value) return '-'

	const parsed = dayjs(value)
	if (!parsed.isValid()) return '-'

	return parsed.format('M/D/YY h:mm A')
}

const displayValue = value => {
	if (value === null || value === undefined || value === '') return '-'

	return value
}

const SortableHeader = ({
	label,
	sortKey,
	sortBy,
	sortDir,
	onSort
}) => {
	const isActive = sortBy === sortKey
	const indicator = isActive ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''

	return (
		<button
			type="button"
			onClick={() => onSort(sortKey)}
			className="inline-flex items-center gap-1 rounded text-left transition hover:text-[#3E63F4] focus:outline-none focus:ring-2 focus:ring-[#3E63F4]/20"
		>
			<span>{label}</span>
			<span aria-hidden="true" className={isActive ? 'text-slate-900' : 'text-slate-400'}>
				{indicator || '↕'}
			</span>
		</button>
	)
}

SortableHeader.propTypes = {
	label: PropTypes.string.isRequired,
	sortKey: PropTypes.string.isRequired,
	sortBy: PropTypes.string.isRequired,
	sortDir: PropTypes.string.isRequired,
	onSort: PropTypes.func.isRequired
}

const LocationEmailAudit = () => {
	const { executeLogout } = useCore()
	const { setShowSpinner } = useGlobal()
	const [searchParams, setSearchParams] = useSearchParams()
	const appliedQuery = useMemo(() => parseAuditSearchParams(searchParams), [searchParams])
	const [auditState, setAuditState] = useState(defaultAuditState)
	const [filters, setFilters] = useState(appliedQuery.filters)
	const [locationOptions, setLocationOptions] = useState([])
	const [isLoading, setIsLoading] = useState(false)
	const latestRequestRef = useRef(0)

	const apiActivityOptions = useMemo(() => ({
		onApiCall: () => setShowSpinner(true),
		onApiResponse: () => setShowSpinner(false)
	}), [setShowSpinner])

	useEffect(() => {
		setFilters(appliedQuery.filters)
	}, [appliedQuery.filters])

	const syncSearchParams = useCallback(nextQuery => {
		setSearchParams(createAuditSearchParams(nextQuery))
	}, [setSearchParams])

	const applyAuditResponse = useCallback((response, currentQuery) => {
		const nextState = {
			items: response?.items || response?.data || [],
			page: response?.page || response?.meta?.currentPage || currentQuery.page,
			pageSize: response?.pageSize || response?.meta?.perPage || currentQuery.pageSize,
			totalItems: response?.totalItems ?? response?.meta?.total ?? 0,
			totalPages: response?.totalPages || response?.meta?.lastPage || 1,
			sortBy: response?.sortBy || currentQuery.sortBy,
			sortDir: response?.sortDir || currentQuery.sortDir,
			statusOptions: response?.statusOptions || [],
			emailTypeOptions: response?.emailTypeOptions || response?.typeOptions || []
		}

		setAuditState(nextState)

		const nextQuery = {
			filters: normalizeAuditFilters({
				...currentQuery.filters,
				...(response?.filters || {})
			}),
			page: nextState.page,
			pageSize: nextState.pageSize,
			sortBy: nextState.sortBy,
			sortDir: nextState.sortDir
		}

		if (!areAuditQueryStatesEqual(currentQuery, nextQuery)) {
			setSearchParams(createAuditSearchParams(nextQuery), { replace: true })
		}
	}, [setSearchParams])

	const loadNotifications = useCallback(currentQuery => {
		const requestId = latestRequestRef.current + 1

		latestRequestRef.current = requestId
		setIsLoading(true)

		Client.OwnerAdminApi.listLocationEmailAudit(
			{
				locationId: currentQuery.filters.locationId,
				status: currentQuery.filters.status,
				emailType: currentQuery.filters.emailType,
				recipient: currentQuery.filters.recipient,
				dateFrom: currentQuery.filters.dateFrom,
				dateTo: currentQuery.filters.dateTo,
				page: currentQuery.page,
				pageSize: currentQuery.pageSize,
				sortBy: currentQuery.sortBy,
				sortDir: currentQuery.sortDir
			},
			{
				status200: response => {
					if (requestId !== latestRequestRef.current) return

					applyAuditResponse(response, currentQuery)
					setIsLoading(false)
				},
				status403: () => {
					if (requestId !== latestRequestRef.current) return

					setIsLoading(false)
					showToast({
						intent: 'error',
						title: 'Location Email Audit',
						content: 'Not authorized to view location email audit data.'
					})
					executeLogout()
				},
				error: error => {
					if (requestId !== latestRequestRef.current) return

					setIsLoading(false)
					console.error(error)
					showToast({
						intent: 'error',
						title: 'Location Email Audit',
						content: 'Unable to load location email audit data.'
					})
				}
			},
			apiActivityOptions
		)
	}, [apiActivityOptions, applyAuditResponse, executeLogout])

	useEffect(() => {
		loadNotifications(appliedQuery)
	}, [appliedQuery, loadNotifications])

	useEffect(() => {
		Client.OwnerAdminApi.listLocations(
			{
				status200: response => {
					setLocationOptions(response || [])
				},
				status403: () => {
					showToast({
						intent: 'error',
						title: 'Location Email Audit',
						content: 'Not authorized to view location email audit data.'
					})
					executeLogout()
				},
				error: error => {
					console.error(error)
					showToast({
						intent: 'error',
						title: 'Location Email Audit',
						content: 'Unable to load locations for the audit filters.'
					})
				}
			},
			{
				onApiCall: null,
				onApiResponse: null
			}
		)
	}, [executeLogout])

	const handleSort = sortKey => {
		const nextSortState = getNextSortState(appliedQuery, sortKey)

		syncSearchParams({
			...appliedQuery,
			...nextSortState
		})
	}

	const buildSortLabel = (label, sortKey) => (
		<SortableHeader
			label={label}
			sortKey={sortKey}
			sortBy={auditState.sortBy}
			sortDir={auditState.sortDir}
			onSort={handleSort}
		/>
	)

	const tableColumns = [
		{
			label: buildSortLabel(SORTABLE_COLUMNS.queuedAt.label, 'queuedAt'),
			name: 'displayQueuedAt',
			formatter: value => formatTimestamp(value)
		},
		{
			label: buildSortLabel(SORTABLE_COLUMNS.emailType.label, 'emailType'),
			name: 'type',
			colClassName: '!whitespace-normal break-all',
			formatter: value => displayValue(value)
		},
		{
			label: buildSortLabel(SORTABLE_COLUMNS.recipient.label, 'recipient'),
			name: 'recipientEmail',
			colClassName: '!whitespace-normal break-all',
			formatter: value => displayValue(value)
		},
		{
			label: buildSortLabel(SORTABLE_COLUMNS.status.label, 'status'),
			name: 'status',
			formatter: value => displayValue(value)
		},
		{
			label: buildSortLabel(SORTABLE_COLUMNS.attempts.label, 'attempts'),
			name: 'attemptCount',
			formatter: value => displayValue(value)
		},
		{
			label: buildSortLabel(SORTABLE_COLUMNS.sentAt.label, 'sentAt'),
			name: 'sentAt',
			formatter: value => formatTimestamp(value)
		},
		{
			label: buildSortLabel(SORTABLE_COLUMNS.reportDate.label, 'reportDate'),
			name: 'reportDate',
			formatter: value => displayValue(value)
		},
		{
			label: 'Last Error',
			name: 'lastError',
			colClassName: '!whitespace-normal',
			formatter: value => displayValue(value)
		},
		{
			label: buildSortLabel(SORTABLE_COLUMNS.location.label, 'location'),
			name: 'locationName',
			colClassName: '!whitespace-normal',
			formatter: value => displayValue(value)
		}
	]

	const tableData = auditState.items.map(row => ({
		...row,
		displayQueuedAt: row.queuedAt || row.createdAt
	}))

	const paginationItems = useMemo(
		() => buildAuditPagination(auditState.page, auditState.totalPages),
		[auditState.page, auditState.totalPages]
	)
	const rowRange = useMemo(
		() => getAuditRowRange(auditState.page, auditState.pageSize, auditState.totalItems, tableData.length),
		[auditState.page, auditState.pageSize, auditState.totalItems, tableData.length]
	)

	const handleFilterChange = event => {
		const { name, value } = event.target

		setFilters(currentFilters => ({
			...currentFilters,
			[name]: value
		}))
	}

	const handleApplyFilters = event => {
		event.preventDefault()

		syncSearchParams({
			...appliedQuery,
			filters: normalizeAuditFilters(filters),
			page: 1
		})
	}

	const handleResetFilters = () => {
		const nextFilters = { ...DEFAULT_FILTERS }

		setFilters(nextFilters)
		syncSearchParams({
			...appliedQuery,
			filters: nextFilters,
			page: 1
		})
	}

	const goToPage = nextPage => {
		if (nextPage < 1 || nextPage > auditState.totalPages) return

		syncSearchParams({
			...appliedQuery,
			page: nextPage
		})
	}

	const handlePageSizeChange = event => {
		syncSearchParams({
			...appliedQuery,
			page: 1,
			pageSize: Number.parseInt(event.target.value, 10) || DEFAULT_PAGE_SIZE
		})
	}

	return (
		<div className="space-y-6">
			<div className="max-w-3xl space-y-2">
				<h1 className="text-xl font-semibold text-slate-900">Location Email Audit</h1>
				<p className="text-sm text-slate-600">
					Read-only audit view for location-related outbound emails, including daily reports and create notifications.
				</p>
			</div>

			<form
				className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-6"
				onSubmit={handleApplyFilters}
			>
				<div className="space-y-1">
					<label className="text-sm font-medium text-slate-700" htmlFor="locationId">
						Location
					</label>
					<select
						id="locationId"
						name="locationId"
						value={filters.locationId}
						onChange={handleFilterChange}
						className={inputClassName}
					>
						<option value="">All locations</option>
						{locationOptions.map(location => (
							<option key={location.id} value={String(location.id)}>
								{location.name}
								{location.licenseNumber ? ` | Lic. #${location.licenseNumber}` : ''}
							</option>
						))}
					</select>
				</div>

				<div className="space-y-1">
					<label className="text-sm font-medium text-slate-700" htmlFor="status">
						Status
					</label>
					<select
						id="status"
						name="status"
						value={filters.status}
						onChange={handleFilterChange}
						className={inputClassName}
					>
						<option value="">All statuses</option>
						{auditState.statusOptions.map(status => (
							<option key={status} value={status}>
								{status}
							</option>
						))}
					</select>
				</div>

				<div className="space-y-1">
					<label className="text-sm font-medium text-slate-700" htmlFor="emailType">
						Email Type
					</label>
					<select
						id="emailType"
						name="emailType"
						value={filters.emailType}
						onChange={handleFilterChange}
						className={inputClassName}
					>
						<option value="">All types</option>
						{auditState.emailTypeOptions.map(type => (
							<option key={type} value={type}>
								{type}
							</option>
						))}
					</select>
				</div>

				<div className="space-y-1">
					<label className="text-sm font-medium text-slate-700" htmlFor="recipient">
						Recipient Email
					</label>
					<input
						id="recipient"
						name="recipient"
						type="text"
						value={filters.recipient}
						onChange={handleFilterChange}
						placeholder="Filter recipient"
						className={inputClassName}
					/>
				</div>

				<div className="space-y-1">
					<label className="text-sm font-medium text-slate-700" htmlFor="dateFrom">
						Date From
					</label>
					<input
						id="dateFrom"
						name="dateFrom"
						type="date"
						value={filters.dateFrom}
						onChange={handleFilterChange}
						className={inputClassName}
					/>
				</div>

				<div className="space-y-1">
					<label className="text-sm font-medium text-slate-700" htmlFor="dateTo">
						Date To
					</label>
					<input
						id="dateTo"
						name="dateTo"
						type="date"
						value={filters.dateTo}
						onChange={handleFilterChange}
						className={inputClassName}
					/>
				</div>

				<div className="flex gap-3 md:col-span-6">
					<Button type="submit">Apply Filters</Button>
					<Button type="button" intent="secondary" onClick={handleResetFilters}>
						Reset
					</Button>
				</div>
			</form>

			<div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
				<div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
					<div className="space-y-1">
						<p className="text-sm text-slate-600">
							Showing {rowRange.start}-{rowRange.end} of {auditState.totalItems} rows
						</p>
						<p className="text-xs text-slate-500">
							Page {auditState.page} of {auditState.totalPages}
						</p>
					</div>

					<div className="flex flex-col gap-3 sm:flex-row sm:items-center">
						<label className="flex items-center gap-2 text-sm text-slate-600" htmlFor="pageSize">
							<span>Rows per page</span>
							<select
								id="pageSize"
								value={auditState.pageSize}
								onChange={handlePageSizeChange}
								className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm outline-none transition focus:border-[#3E63F4] focus:ring-2 focus:ring-[#3E63F4]/20"
							>
								{PAGE_SIZE_OPTIONS.map(option => (
									<option key={option} value={option}>
										{option}
									</option>
								))}
							</select>
						</label>
					</div>
				</div>

				{isLoading ? (
					<div className="rounded-xl border border-dashed border-slate-200 px-4 py-16 text-center text-sm text-slate-500">
						Loading location email audit rows...
					</div>
				) : tableData.length === 0 ? (
					<div className="rounded-xl border border-dashed border-slate-200 px-4 py-16 text-center text-sm text-slate-500">
						No location email rows matched the current filters.
					</div>
				) : (
					<Table columns={tableColumns} data={tableData} striped />
				)}

				<div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
					<div className="flex flex-wrap justify-end gap-2">
						{paginationItems.map((item, index) =>
							item === 'ellipsis' ? (
								<span
									key={`ellipsis-${index}`}
									className="px-2 py-1 text-sm text-slate-400"
								>
									...
								</span>
							) : (
								<Button
									key={item}
									type="button"
									size="sm"
									intent={item === auditState.page ? 'primary' : 'secondary'}
									onClick={() => goToPage(item)}
								>
									{item}
								</Button>
							)
						)}
					</div>

					<div className="flex justify-end gap-3">
						<Button
							type="button"
							intent="secondary"
							onClick={() => goToPage(auditState.page - 1)}
							disabled={auditState.page <= 1}
						>
							Previous
						</Button>
						<Button
							type="button"
							intent="secondary"
							onClick={() => goToPage(auditState.page + 1)}
							disabled={auditState.page >= auditState.totalPages}
						>
							Next
						</Button>
					</div>
				</div>
			</div>
		</div>
	)
}

export default LocationEmailAudit
